import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  PaymentElement,
  AddressElement,
} from '@stripe/react-stripe-js';
import { stripeConfig, ELEMENTS_OPTIONS } from '../config/stripeConfig';
import { useNotification } from '../contexts/NotificationContext';
import { useBilling } from '../contexts/BillingContext';

// Types
interface PaymentFormProps {
  amount: number;
  currency: string;
  onSuccess: (paymentIntent: any) => void;
  onError: (error: string) => void;
  isSubscription?: boolean;
  customerId?: string;
  description?: string;
  className?: string;
}

interface PaymentFormInnerProps {
  amount: number;
  currency: string;
  onSuccess: (paymentIntent: any) => void;
  onError: (error: string) => void;
  isSubscription?: boolean;
  customerId?: string;
  description?: string;
}

// Inner component that uses Stripe hooks
const PaymentFormInner: React.FC<PaymentFormInnerProps> = ({
  amount,
  currency,
  onSuccess,
  onError,
  isSubscription = false,
  customerId,
  description,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { showSuccess, showError } = useNotification();
  const { addPaymentMethod } = useBilling();

  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Create payment intent or setup intent
  useEffect(() => {
    const createIntent = async () => {
      try {
        if (isSubscription && customerId) {
          // For subscriptions, create a setup intent to save payment method
          const response = await fetch('/api/stripe/create-setup-intent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ customerId }),
          });
          const { clientSecret } = await response.json();
          setClientSecret(clientSecret);
        } else {
          // For one-time payments, create a payment intent
          const response = await fetch('/api/stripe/create-payment-intent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount: Math.round(amount * 100), // Convert to cents
              currency: currency.toLowerCase(),
              customerId,
            }),
          });
          const { clientSecret } = await response.json();
          setClientSecret(clientSecret);
        }
      } catch (error) {
        console.error('Error creating intent:', error);
        onError('Failed to initialize payment');
      }
    };

    createIntent();
  }, [amount, currency, isSubscription, customerId, onError]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setIsProcessing(true);

    try {
      if (isSubscription) {
        // Handle setup intent for subscriptions
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/billing`,
          },
          redirect: 'if_required',
        });

        if (error) {
          onError(error.message || 'Payment failed');
          return;
        }

        if (setupIntent?.status === 'succeeded') {
          // Save payment method to customer
          await addPaymentMethod(setupIntent.payment_method as string);
          showSuccess('Payment method added successfully!');
          onSuccess(setupIntent);
        }
      } else {
        // Handle payment intent for one-time payments
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/billing`,
          },
          redirect: 'if_required',
        });

        if (error) {
          onError(error.message || 'Payment failed');
          return;
        }

        if (paymentIntent?.status === 'succeeded') {
          showSuccess('Payment successful!');
          onSuccess(paymentIntent);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      onError('An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-2 text-gray-600">Loading payment form...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {isSubscription ? 'Add Payment Method' : 'Payment Details'}
        </h3>
        {description && (
          <p className="text-sm text-gray-600 mb-4">{description}</p>
        )}
        <div className="text-lg font-semibold text-gray-900">
          {isSubscription ? 'No immediate charge' : `$${amount.toFixed(2)} ${currency.toUpperCase()}`}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Information
          </label>
          <div className="p-3 border border-gray-300 rounded-md">
            <PaymentElement
              options={{
                layout: 'tabs',
                fields: {
                  billingDetails: {
                    name: 'auto',
                    email: 'auto',
                    phone: 'auto',
                    address: 'auto',
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-purple-600 text-white py-3 px-4 rounded-md font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Processing...
          </div>
        ) : (
          isSubscription ? 'Add Payment Method' : `Pay $${amount.toFixed(2)}`
        )}
      </button>

      <div className="text-xs text-gray-500 text-center">
        Your payment information is secure and encrypted.
      </div>
    </form>
  );
};

// Main PaymentForm component
const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  currency,
  onSuccess,
  onError,
  isSubscription = false,
  customerId,
  description,
  className = '',
}) => {
  const [stripePromise, setStripePromise] = useState<any>(null);

  useEffect(() => {
    const loadStripeInstance = async () => {
      if (stripeConfig.publishableKey) {
        const stripe = await loadStripe(stripeConfig.publishableKey);
        setStripePromise(stripe);
      } else {
        console.warn('Stripe publishable key not found. Payment functionality will be disabled.');
        setStripePromise(null);
      }
    };

    loadStripeInstance();
  }, []);

  if (!stripePromise) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="text-gray-500 mb-4">
          <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="text-lg font-medium">Payment System Not Configured</p>
          <p className="text-sm text-gray-400 mt-1">
            Stripe publishable key is missing. Please configure your Stripe keys to enable payments.
          </p>
        </div>
      </div>
    );
  }

  const elementsOptions = {
    ...ELEMENTS_OPTIONS,
    currency: currency.toLowerCase(),
    amount: Math.round(amount * 100),
  };

  return (
    <div className={className}>
      <Elements stripe={stripePromise} options={elementsOptions}>
        <PaymentFormInner
          amount={amount}
          currency={currency}
          onSuccess={onSuccess}
          onError={onError}
          isSubscription={isSubscription}
          customerId={customerId}
          description={description}
        />
      </Elements>
    </div>
  );
};

export default PaymentForm;

